import React from "react";
import { captureError } from "@/lib/posthog";

interface Props {
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
}

/**
 * Top-level React error boundary that reports handled render failures to
 * PostHog Error Tracking via `posthog.captureException`.
 *
 * Unhandled `window.onerror` / `unhandledrejection` exceptions are captured by
 * PostHog's own automatic exception capture, which is enabled from the PostHog
 * project's Error Tracking settings (see docs).
 *
 * Only sanitized, non-sensitive context is attached: the component stack digest
 * and the current route. No application state, no user data.
 */
export default class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureError(error, {
      boundary: "app_root",
      component_stack_depth: (info.componentStack ?? "").split("\n").length,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
          <h1 className="font-heading text-xl text-foreground">Something went wrong</h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            We hit an unexpected error. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-border px-4 py-2 text-sm text-foreground"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
